import { DEVICE_ID_PATTERN } from "@fts/core";
import type { DeviceRegistration } from "../data/provider";

/** "3 minutes ago" / "3 dakika önce" style relative time; null for missing values. */
export function relativeTime(iso: string | null, locale: string, now = Date.now()): string | null {
  if (!iso) return null;
  const t = Date.parse(iso);
  if (Number.isNaN(t)) return null;
  const diff = (t - now) / 1000;
  const abs = Math.abs(diff);
  const rtf = new Intl.RelativeTimeFormat(locale, { numeric: "auto" });
  if (abs < 45) return rtf.format(Math.round(diff), "second");
  if (abs < 45 * 60) return rtf.format(Math.round(diff / 60), "minute");
  if (abs < 22 * 3600) return rtf.format(Math.round(diff / 3600), "hour");
  if (abs < 26 * 86400) return rtf.format(Math.round(diff / 86400), "day");
  if (abs < 320 * 86400) return rtf.format(Math.round(diff / (30 * 86400)), "month");
  return rtf.format(Math.round(diff / (365 * 86400)), "year");
}

export function formatDate(iso: string | null, locale: string): string | null {
  if (!iso) return null;
  const d = new Date(iso);
  return Number.isNaN(d.getTime()) ? null : d.toLocaleDateString(locale, { day: "2-digit", month: "short", year: "numeric" });
}

export function formatDateTime(iso: string | null, locale: string): string | null {
  if (!iso) return null;
  const d = new Date(iso);
  return Number.isNaN(d.getTime())
    ? null
    : d.toLocaleString(locale, { day: "2-digit", month: "short", year: "numeric", hour: "2-digit", minute: "2-digit" });
}

/* ---------------- tenant slug ---------------- */

export const SLUG_PATTERN = /^[a-z0-9][a-z0-9-]{1,40}$/;

const TR_MAP: Record<string, string> = { ç: "c", ğ: "g", ı: "i", ö: "o", ş: "s", ü: "u", â: "a", î: "i", û: "u" };

/** URL-safe slug suggestion from a company name ("Örnek Lojistik A.Ş." → "ornek-lojistik-as"). */
export function slugify(name: string): string {
  const lowered = name.replace(/İ/g, "i").replace(/I/g, "ı").toLocaleLowerCase("tr");
  const ascii = Array.from(lowered, (ch) => TR_MAP[ch] ?? ch)
    .join("")
    .normalize("NFKD")
    .replace(/[̀-ͯ]/g, "");
  return ascii
    .replace(/[^a-z0-9]+/g, (m) => (/[.'’]/.test(m) && m.length === 1 ? "" : "-"))
    .replace(/-+/g, "-")
    .replace(/^-|-$/g, "")
    .slice(0, 41)
    .replace(/-$/, "");
}

/* ---------------- device registration input ---------------- */

export type RegistrationLineError = { line: number; text: string; reason: "invalid_device_id" | "duplicate" | "too_many_fields" };

/**
 * Parse the bulk registration textarea: one device per line, `DEVICE_ID[,IMEI[,ICCID]]`.
 * Blank lines are ignored; ids are upper-cased (labels are printed in upper case).
 */
export function parseRegistrationLines(text: string): { rows: DeviceRegistration[]; errors: RegistrationLineError[] } {
  const rows: DeviceRegistration[] = [];
  const errors: RegistrationLineError[] = [];
  const seen = new Set<string>();
  text.split(/\r?\n/).forEach((raw, i) => {
    const line = raw.trim();
    if (!line) return;
    const parts = line.split(/[,;\t]/).map((p) => p.trim());
    const id = (parts[0] ?? "").toUpperCase();
    if (parts.length > 3) errors.push({ line: i + 1, text: line, reason: "too_many_fields" });
    else if (!DEVICE_ID_PATTERN.test(id)) errors.push({ line: i + 1, text: line, reason: "invalid_device_id" });
    else if (seen.has(id)) errors.push({ line: i + 1, text: line, reason: "duplicate" });
    else {
      seen.add(id);
      rows.push({ deviceId: id, imei: parts[1] || null, iccid: parts[2] || null });
    }
  });
  return { rows, errors };
}

/* ---------------- CSV export ---------------- */

function csvCell(v: string): string {
  return /[",\r\n]/.test(v) ? `"${v.replace(/"/g, '""')}"` : v;
}

export function toCsv(header: string[], rows: string[][]): string {
  return [header, ...rows].map((r) => r.map(csvCell).join(",")).join("\r\n") + "\r\n";
}

/** `fts-cihaz-kayit-20261116-0930.csv` (local time). */
export function registrationCsvName(d = new Date()): string {
  const p = (n: number) => String(n).padStart(2, "0");
  return `fts-cihaz-kayit-${d.getFullYear()}${p(d.getMonth() + 1)}${p(d.getDate())}-${p(d.getHours())}${p(d.getMinutes())}.csv`;
}

/** Client-side file download (no server round trip, nothing stored). */
export function downloadText(filename: string, content: string, type = "text/csv;charset=utf-8"): void {
  // BOM so spreadsheet apps detect UTF-8.
  const blob = new Blob(["﻿", content], { type });
  const url = URL.createObjectURL(blob);
  const a = document.createElement("a");
  a.href = url;
  a.download = filename;
  document.body.appendChild(a);
  a.click();
  a.remove();
  setTimeout(() => URL.revokeObjectURL(url), 1000);
}

export function inviteLink(token: string, loc: Pick<Location, "origin" | "pathname"> = window.location): string {
  return `${loc.origin}${loc.pathname}#/invite/${token}`;
}
